function normalizeDate(value) {
  const text = String(value || "").trim();
  if (!text) {
    return null;
  }
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
}

export function captureConversationVersion(snapshot) {
  if (!snapshot) {
    return null;
  }
  return {
    id: snapshot.id || null,
    contactId: snapshot.contact_id || null,
    status: snapshot.status || null,
    assignedUserId: snapshot.assigned_user_id || null,
    agingBucket: snapshot.aging_bucket || null,
    firstResponseOverdue: Boolean(snapshot.first_response_overdue),
    lastMessageAt: normalizeDate(snapshot.last_message_at),
    updatedAt: normalizeDate(snapshot.updated_at),
  };
}

export function describeConversationDrift(previousSnapshot, nextSnapshot) {
  const previous = captureConversationVersion(previousSnapshot);
  const next = captureConversationVersion(nextSnapshot);
  if (!previous || !next || previous.id !== next.id) {
    return null;
  }
  const changes = [];
  if (previous.status !== next.status) {
    changes.push(next.status === "closed" ? "record was closed" : `status changed to ${next.status}`);
  }
  if (previous.assignedUserId !== next.assignedUserId) {
    changes.push(next.assignedUserId ? "ownership changed" : "record was released to the queue");
  }
  if (previous.agingBucket !== next.agingBucket && next.agingBucket) {
    changes.push(`SLA moved to ${next.agingBucket}`);
  }
  if (previous.firstResponseOverdue !== next.firstResponseOverdue) {
    changes.push(next.firstResponseOverdue ? "first response is now overdue" : "first response is back within target");
  }
  if (previous.lastMessageAt !== next.lastMessageAt && next.lastMessageAt) {
    changes.push("new message activity arrived");
  }
  if (!changes.length) {
    return null;
  }
  const tone = next.status === "closed" || next.firstResponseOverdue ? "overdue" : "stale";
  return {
    tone,
    message: `This record changed in another session: ${changes.join(", ")}.`,
    changes,
  };
}

export function buildSyncStatusSummary(options = {}) {
  const settings = Object.assign(
    {
      active: false,
      intervalMs: 0,
      lastSyncedAt: null,
      noticeCount: 0,
      visible: true,
    },
    options
  );
  if (!settings.active) {
    return "Live sync inactive until a CRM session is established.";
  }
  const seconds = Math.max(1, Math.round((settings.intervalMs || 0) / 1000));
  const syncText = settings.lastSyncedAt ? `Last synced ${new Date(settings.lastSyncedAt).toLocaleTimeString()}.` : "Waiting for first live sync.";
  const visibilityText = settings.visible ? "Auto-refresh is active." : "Auto-refresh pauses while this tab is hidden.";
  const driftText = settings.noticeCount > 0 ? `${settings.noticeCount} record change notice(s) pending.` : "No cross-session changes detected.";
  return `${visibilityText} Checking every ${seconds}s. ${syncText} ${driftText}`;
}
